// Moved from `src/hooks/mutation/useMutateCreateUser.ts` (now the app delegate)
// with the identity D2 slice (ARCFE-1062). Unblocked by D1 (ARCFE-1037), which
// carved `createUser` into `../api/user`.
//
// Every effect this hook had is an app reaction, so the package keeps exactly
// what is workspace's: the POST /user request and its mutation identity.
// Severed per ARCFE-921: the Rematch `analyticsScribe.demoBindOnboardingInfo`
// dispatch, and the `router.push('/scribe')` error recovery — a ROUTER call,
// which `/data` is forbidden by construction (task 1.4's exclusion list).
// `invalidateCurrentUser()` (composite: GET_USER + the commerce GET_PRODUCTS)
// and `invalidateKindeProperties()` (an UNCARVED app-shell key) travel with it
// in the same onSuccess port, in their legacy order.
import { useMutation } from '@tanstack/react-query';

import { type CreateUserRequest } from '../../contracts';
import { createUser } from '../api/user';

export interface UseMutateCreateUserOptions {
  /**
   * App-reaction port: fired where `invalidateCurrentUser()` →
   * `invalidateKindeProperties()` → the Rematch onboarding-info dispatch fired,
   * in that order.
   */
  onUserCreated?: () => void;
  /**
   * App-reaction port: fired where the legacy `router.push('/scribe')` recovery
   * fired. A router is app-owned — `/data` never navigates.
   */
  onCreateUserError?: () => void;
}

/** POST /user */
export function useMutateCreateUser(options?: UseMutateCreateUserOptions) {
  return useMutation({
    mutationFn: async (variables: CreateUserRequest) => {
      const data = await createUser(variables);
      return data;
    },
    onSuccess: () => {
      options?.onUserCreated?.();
    },
    onError: () => {
      options?.onCreateUserError?.();
    },
  });
}
